import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "row",
    "totalTime",
    "caseCount",
    "scriptCount",
    "expandCollapseAllButton",
  ];

  static values = {
    timeFormat: { type: String, default: "long" },
  };

  connect() {
    this.updateTotalTime();
    this.updateCounts();
    // Listen for student-choice-cases:changed event
    this.element.addEventListener('student-choice-cases:changed', () => {
      this.updateTotalTime();
    });
  }

  remove(event) {
    const button = event.currentTarget
    const documentId = button.dataset.documentId

    // Remove hidden input
    const hiddenInputs = document.querySelectorAll('input[name="document_set[document_ids][]"]')
    hiddenInputs.forEach((input) => {
      if (input.value === documentId) input.remove()
    })

    // Find the row
    const row = this.rowTargets.find((el) => el.dataset.documentId === documentId)
    if (!row) return

    // Remove line before
    const findPrevCourseLine = (el) => {
      let prev = el.previousElementSibling
      while (prev && !prev.classList.contains("course-line")) {
        prev = prev.previousElementSibling
      }
      return prev
    }
    findPrevCourseLine(row)?.remove()

    // Remove row
    row.remove()

    this.toggleNoContentMessage();

    // Recalculate everything
    this.updateTotalTime()
    this.updateCounts()
    this.appendToRemovedStudents(documentId)
  }

  toggleNoContentMessage() {
    // Only consider rows that are NOT student_choice_case
    const nonStudentChoiceRows = this.rowTargets.filter(
      (row) => row.dataset.documentType !== "student_choice_case"
    );
    // Hide expandCollapseAllButton if no non-student-choice rows left
    if (nonStudentChoiceRows.length === 0) {
      this.expandCollapseAllButtonTarget.style.display = "none";
      // Show 'no content' message if present, else insert it
      let message = document.querySelector(".course-card-description");
      if (message) {
        message.style.display = "";
      } else {
        const container = document.querySelector(".course-card-content");
        if (container) {
          const span = document.createElement("span");
          span.className = "course-card-description";
          span.textContent = "You have not added any content yet.";
          container.appendChild(span);
        }
      }
    } else {
      // Hide the message if rows exist
      let message = document.querySelector(".course-card-description");
      if (message) {
        message.style.display = "none";
      }
    }
  }

  updateTotalTime() {
    let total = 0
    this.rowTargets.forEach((row) => {
      const time = parseInt(row.dataset.documentTime) || 0
      total += time
    })

    const roundedMinutes = Math.round(total / 60)
    const hours = Math.floor(roundedMinutes / 60)
    const minutes = roundedMinutes % 60

    const timeString = this.compactTimeFormat
      ? this.compactTimeString(hours, minutes)
      : `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`
    this.totalTimeTargets.forEach((el) => el.textContent = timeString)
  }

  get compactTimeFormat() {
    return this.timeFormatValue === "compact"
  }

  compactTimeString(hours, minutes) {
    const parts = []
    if (hours > 0) parts.push(`${hours}h`)
    if (minutes > 0) parts.push(`${minutes}min`)
    return parts.length ? parts.join(" ") : "0min"
  }

  updateCounts() {
    let caseCount = 0
    let scriptCount = 0

    this.rowTargets.forEach((row) => {
      const type = row.dataset.documentType
      if (type === "signature_case") caseCount++
      else if (type === "illness_script") scriptCount++
    })

    const caseLabel = this.compactTimeFormat
      ? `${caseCount} Cases`
      : `${caseCount} Case${caseCount !== 1 ? 's' : ''}`
    const scriptLabel = this.compactTimeFormat
      ? `${scriptCount} Scripts`
      : `${scriptCount} Script${scriptCount !== 1 ? 's' : ''}`

    this.caseCountTargets.forEach((el) =>
      el.textContent = caseLabel
    )
    this.scriptCountTargets.forEach((el) =>
      el.textContent = scriptLabel
    )
  }

  appendToRemovedStudents(doc_id) {
    const container = document.getElementById("remove-document-container");
    if (!container) return;

    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "remove_document_ids[]";
    input.value = doc_id;
    container.appendChild(input);
  };
}
